// @rpg-unit/creature-presets: six creatures for a farm or a village (tier 3; IMP-0059, gap G13 of dry run 1): a dog, a
// cat, a goat, a cow, a chicken and a bee, as knob sets for @rpg-unit/creature-bodies' recipes, with the poses each
// uses at rest. The table (`src/content/tables/creatures.json`) is content: a game lists this package in its content
// `include.json`, or reads `CREATURE_PRESETS`. `creatureCard(id)` makes a preset's @rpg/figure card; the four-legged
// ones move on @rpg/figure's four-legged motion (`card.quad`), the chicken and the bee on the biped motion
// (`card.motion`). Tune one by spreading its knobs: `creatureCard({ ...preset("dog"), knobs: { ...k, height: 0.4 } })`.
import type { FigureCard } from "@rpg/figure";
import { birdCard, flyerCard, quadrupedCard, type BirdKnobs, type FlyerKnobs, type QuadKnobs } from "@rpg-unit/creature-bodies";
import table from "./content/tables/creatures.json";

/** This unit's version (the manifest's and package.json's). */
export const CREATURE_PRESETS_VERSION = "0.1.0" as const;

/** Planted defects beside what they break: the presets' checks must go red with them on. */
export const PRESET_FAULTS = {
  /** Every row is built from the first row's knobs: the cat is a dog's size. */
  rowsShareKnobs: false,
};

/** A preset, as its schema describes it (`src/content/schemas/creatures.schema.json`). */
export type CreaturePreset =
  | { id: string; plan: "quadruped"; poses: ("sit" | "lie" | "eat")[]; knobs: QuadKnobs; reason?: string }
  | { id: string; plan: "bird"; poses: ("sit" | "lie" | "eat")[]; knobs: BirdKnobs; reason?: string }
  | { id: string; plan: "flyer"; poses: ("sit" | "lie" | "eat")[]; knobs: FlyerKnobs; reason?: string };

/** Every preset, in the table's order. */
export const CREATURE_PRESETS = table.rows as CreaturePreset[];

/** A preset by id (throws on an unknown one). */
export function preset(id: string): CreaturePreset {
  const p = CREATURE_PRESETS.find((r) => r.id === id);
  if (!p) throw new Error(`creature-presets: no preset "${id}" (have ${CREATURE_PRESETS.map((r) => r.id).join(", ")})`);
  return p;
}

/** A preset's @rpg/figure card (by id, or a row a game tuned). */
export function creatureCard(p: string | CreaturePreset): FigureCard {
  const row = typeof p === "string" ? preset(p) : p;
  const first = CREATURE_PRESETS.find((r) => r.plan === row.plan)!;
  const knobs = PRESET_FAULTS.rowsShareKnobs ? first.knobs : row.knobs;
  if (row.plan === "quadruped") return quadrupedCard(knobs as QuadKnobs);
  if (row.plan === "bird") return birdCard(knobs as BirdKnobs);
  return flyerCard(knobs as FlyerKnobs);
}
